import React, { useState } from 'react';
import styles from './GroupRow.module.scss';
import classNames from 'classnames';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import { getFullName } from '../../utils/dashboardUtils';
import ConfirmationDialog from '../DialogWrappers/ConfirmationDialog';
import Menu from '../atoms/Menu';
import AssignProgramDialog from '../Programs/AssignProgramDialog';
import SelectMembersDialog from './SelectMembersDialog';
import { useUserAction } from '../../providers';
import AssignProgramOptionsDialog from '../Programs/AssignProgramOptionsDialog';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';

import type { TrainerDashboardData } from '../../appState/appInitialState';
import type { Group, AssignedProgram, WithOptionalId } from '../../types';

interface GroupRowProps {
  group: Group;
  selected: boolean;
  onSelect?: () => void;
  trainers: TrainerDashboardData[];
  openEditGroupModal: () => void;
  deleteGroup: (group: Group) => Promise<void>;
  allowDelete: boolean;
  onEditProgram: () => void;
  onCreateProgram: () => void;
  onSubmitEditedGroup: (
    currentGroupData: WithOptionalId<Group>,
    newGroupData: WithOptionalId<Group>,
  ) => Promise<boolean>;
}

const GroupRow: React.FC<GroupRowProps> = ({
  group,
  trainers,
  onSelect,
  selected,
  deleteGroup,
  openEditGroupModal,
  allowDelete,
  onEditProgram,
  onCreateProgram,
  onSubmitEditedGroup,
}) => {
  const { assignProgramAndRefreshMembers } = useUserAction();
  const [isDeleteGroupConfirmationOpen, setIsDeleteGroupConfirmationOpen] =
    useState(false);
  const [isAssignProgramOptionsModalOpen, setIsAssignProgramOptionsModalOpen] =
    useState(false);
  const [isAddProgramTemplateModalOpen, setIsAddProgramTemplateModalOpen] =
    useState(false);
  const [isSelectMembersModalOpen, setIsSelectMembersModalOpen] =
    useState(false);
  const [programFromTemplate, setProgramFromTemplate] =
    useState<AssignedProgram | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleCloseMenuOptions = () => {
    setAnchorEl(null);
  };

  const renderGroupName = () => (
    <div className={classNames(styles.groupColumn, styles.groupName)}>
      {group.name}
    </div>
  );

  const renderTrainerName = () => {
    const trainer = trainers
      .map(({ trainer }) => trainer)
      .find(({ id }) => id === group.trainerIds[0]);
    const trainerName = getFullName(trainer);
    return <div className={classNames(styles.groupColumn)}>{trainerName}</div>;
  };

  const renderClients = () => {
    return (
      <div
        className={classNames(styles.groupColumn)}
      >{`${group.memberIds.length} Members`}</div>
    );
  };

  const renderProgram = () => {
    return (
      <div className={classNames(styles.groupColumn)}>
        {group?.program?.name}
      </div>
    );
  };

  const handleEditGroup = () => {
    openEditGroupModal();
    handleCloseMenuOptions();
  };

  const handleDeleteGroup = async () => {
    handleCloseMenuOptions();
    await deleteGroup(group);
    setIsDeleteGroupConfirmationOpen(false);
  };

  const handleSubmitProgramTemplate = async (program: AssignedProgram) => {
    setProgramFromTemplate(program);
    setIsAddProgramTemplateModalOpen(false);
    setIsSelectMembersModalOpen(true);
  };

  const renderProgramTemplateDialog = () => (
    <AssignProgramDialog
      onSubmit={handleSubmitProgramTemplate}
      isOpen={isAddProgramTemplateModalOpen}
      onClose={() => setIsAddProgramTemplateModalOpen(false)}
    />
  );

  const assignProgramToSelectedMembers = async (memberIds: string[]) => {
    if (!programFromTemplate) {
      setIsSelectMembersModalOpen(false);
      return;
    }
    const updatedGroup = {
      ...group,
      program: programFromTemplate,
    };
    await Promise.all([
      assignProgramAndRefreshMembers(memberIds, programFromTemplate),
      onSubmitEditedGroup(group as Group, updatedGroup as Group),
    ]);
    setProgramFromTemplate(null);
    setIsSelectMembersModalOpen(false);
  };

  const handleCloseSelectMembersDialog = () => {
    setProgramFromTemplate(null);
    setIsSelectMembersModalOpen(false);
  };

  const renderSelectMembersDialog = () => (
    <SelectMembersDialog
      onSubmit={assignProgramToSelectedMembers}
      isOpen={isSelectMembersModalOpen}
      onClose={handleCloseSelectMembersDialog}
      memberIds={group.memberIds}
    />
  );

  const renderAssignProgramOptionsDialog = () => {
    if (!isAssignProgramOptionsModalOpen) return null;
    return (
      <AssignProgramOptionsDialog
        onCreate={() => {
          setIsAssignProgramOptionsModalOpen(false);
          onCreateProgram();
        }}
        onAdd={() => {
          setIsAssignProgramOptionsModalOpen(false);
          setIsAddProgramTemplateModalOpen(true);
        }}
        onClose={() => setIsAssignProgramOptionsModalOpen(false)}
      />
    );
  };

  const renderMoreOptions = () => {
    const menuOptions = [
      {
        onClick: handleEditGroup,
        label: 'Manage Group',
        icon: <BorderColorOutlinedIcon color="primary" />,
      },
      {
        onClick: () => {
          handleCloseMenuOptions();
          setIsAssignProgramOptionsModalOpen(true);
        },
        label: 'Assign Program',
        icon: <PersonAddAlt1OutlinedIcon color="primary" />,
      },
    ];

    if (group.program) {
      menuOptions.push({
        onClick: () => {
          handleCloseMenuOptions();
          onEditProgram();
        },
        label: 'Edit Program',
        icon: <AppRegistrationIcon color="primary" />,
      });
    }

    if (allowDelete) {
      menuOptions.push({
        onClick: () => {
          handleCloseMenuOptions();
          setIsDeleteGroupConfirmationOpen(true);
        },
        label: 'Delete Group',
        icon: <DeleteForeverOutlinedIcon color="primary" />,
      });
    }

    return (
      <div className={classNames(styles.groupColumn, styles.options)}>
        <PendingOutlinedIcon
          className={styles.optionsBtn}
          color="primary"
          onClick={(event) => {
            event.stopPropagation();
            setAnchorEl(event.currentTarget as unknown as HTMLElement);
          }}
        />
        <Menu
          anchorEl={anchorEl}
          isOpen={open}
          onClose={handleCloseMenuOptions}
          options={menuOptions}
        />
      </div>
    );
  };

  const renderDeleteGroupConfirmationDialog = () => (
    <ConfirmationDialog
      text="Are you sure you want to delete this group?"
      onSubmit={handleDeleteGroup}
      submitText="Yes"
      cancelText="No"
      isOpen={isDeleteGroupConfirmationOpen}
      onCancel={() => setIsDeleteGroupConfirmationOpen(false)}
      symbol={<DeleteForeverOutlinedIcon />}
    />
  );

  const handleRowClick = () => {
    // Don't trigger row selection if menu is open
    // Let the menu close naturally when clicking away
    if (!open && onSelect) {
      onSelect();
    }
  };

  return (
    <>
      <div
        onClick={handleRowClick}
        className={classNames(styles.groupRow, styles.listGrid, {
          [styles.selected]: selected,
        })}
      >
        {renderGroupName()}
        {renderClients()}
        {renderTrainerName()}
        {renderProgram()}
        {renderMoreOptions()}
      </div>
      {renderDeleteGroupConfirmationDialog()}
      {renderAssignProgramOptionsDialog()}
      {renderProgramTemplateDialog()}
      {renderSelectMembersDialog()}
    </>
  );
};

export default GroupRow;
